/** @jsxImportSource @emotion/react */
import { css, type Interpolation } from '@emotion/react';
import {
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

export const DROPDOWN_MENU_COLORS = {
  text: '#1d1d1d',
  textMuted: '#a0a0a0',
  border: '#d4d4d4',
  hoverBackground: '#ebebeb',
  pressedBackground: '#d4d4d4',
  white: '#ffffff',
} as const;

export const DROPDOWN_MENU_SURFACE_STYLE = css`
  label: DROPDOWN_MENU_SURFACE_STYLE;
  border: 1px solid ${DROPDOWN_MENU_COLORS.border};
  border-radius: 5px;
  background: ${DROPDOWN_MENU_COLORS.white};
  box-shadow:
    0 4px 6px rgba(29, 29, 29, 0.1),
    0 10px 15px rgba(29, 29, 29, 0.1);
`;

export const DROPDOWN_MENU_ITEM_STYLE = css`
  label: DROPDOWN_MENU_ITEM_STYLE;
  width: 100%;
  border: 0;
  display: inline-flex;
  align-items: center;
  background: ${DROPDOWN_MENU_COLORS.white};
  color: ${DROPDOWN_MENU_COLORS.textMuted};
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${DROPDOWN_MENU_COLORS.hoverBackground};
    color: ${DROPDOWN_MENU_COLORS.text};
    outline: none;
  }

  &:active {
    background: ${DROPDOWN_MENU_COLORS.pressedBackground};
    color: ${DROPDOWN_MENU_COLORS.text};
  }

  &:disabled {
    cursor: default;
  }

  &:disabled:hover,
  &:disabled:focus-visible,
  &:disabled:active {
    background: ${DROPDOWN_MENU_COLORS.white};
  }
`;

export const DROPDOWN_MENU_ITEM_SELECTED_STYLE = css`
  label: DROPDOWN_MENU_ITEM_SELECTED_STYLE;
  color: ${DROPDOWN_MENU_COLORS.text};
`;

export const DROPDOWN_MENU_ITEM_LEADING_STYLE = css`
  label: DROPDOWN_MENU_ITEM_LEADING_STYLE;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const DROPDOWN_MENU_ITEM_LABEL_STYLE = css`
  label: DROPDOWN_MENU_ITEM_LABEL_STYLE;
  color: currentColor;
`;

type UseDropdownMenuOptions = {
  initialOpen?: boolean;
};

export const useDropdownMenu = ({
  initialOpen = false,
}: UseDropdownMenuOptions = {}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(initialOpen);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: globalThis.MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    window.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const openMenu = useCallback(() => setOpen(true), []);
  const closeMenu = useCallback(() => setOpen(false), []);
  const toggleMenu = useCallback(() => setOpen(value => !value), []);

  return {
    containerRef,
    open,
    setOpen,
    openMenu,
    closeMenu,
    toggleMenu,
  };
};

type DropdownMenuSurfaceProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  surfaceCss?: Interpolation<any>;
};

export const DropdownMenuSurface = ({
  children,
  surfaceCss,
  ...props
}: DropdownMenuSurfaceProps) => (
  <div css={[DROPDOWN_MENU_SURFACE_STYLE, surfaceCss]} {...props}>
    {children}
  </div>
);

type DropdownMenuItemProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  leading?: ReactNode;
  itemCss?: Interpolation<any>;
  leadingCss?: Interpolation<any>;
  labelCss?: Interpolation<any>;
  selected?: boolean;
  selectedLabelCss?: Interpolation<any>;
};

export const DropdownMenuItem = ({
  children,
  leading,
  itemCss,
  leadingCss,
  labelCss,
  selected = false,
  selectedLabelCss,
  type = 'button',
  ...props
}: DropdownMenuItemProps) => (
  <button
    type={type}
    css={[
      DROPDOWN_MENU_ITEM_STYLE,
      selected && DROPDOWN_MENU_ITEM_SELECTED_STYLE,
      itemCss,
    ]}
    {...props}
  >
    {leading && (
      <span css={[DROPDOWN_MENU_ITEM_LEADING_STYLE, leadingCss]}>
        {leading}
      </span>
    )}
    <span
      css={[
        DROPDOWN_MENU_ITEM_LABEL_STYLE,
        labelCss,
        selected && selectedLabelCss,
      ]}
    >
      {children}
    </span>
  </button>
);
